import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Button } from "./ui/button";
import { ArrowLeft, Loader2, ExternalLink, TrendingUp, TrendingDown, Minus, Download, Calendar } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_venture-mentor-2/artifacts/52nzs5st_ChatGPT%20Image%20Jan%2022%2C%202026%2C%2002_22_56%20PM.png";

const ResultsDashboard = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEvaluation();
  }, [sessionId]);

  const fetchEvaluation = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/evaluation/${sessionId}`);
      setEvaluation(response.data);
    } catch (error) {
      toast.error("Failed to load evaluation results.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return "text-[#10B981]";
    if (score >= 50) return "text-[#C5A059]";
    return "text-[#EF4444]";
  };

  const getThreatIcon = (level) => {
    if (level === "High") return <TrendingUp className="h-4 w-4 text-[#EF4444]" />;
    if (level === "Medium") return <Minus className="h-4 w-4 text-[#C5A059]" />;
    return <TrendingDown className="h-4 w-4 text-[#10B981]" />;
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/evaluation/${sessionId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evaluation_${sessionId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download PDF");
      console.error(error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#0047AB] mx-auto mb-4" />
          <p className="text-[#64748B]">Loading evaluation results...</p>
        </div>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#1A1A1A] mb-4">Evaluation not found.</p>
          <Button onClick={() => navigate("/")} className="bg-[#0047AB] hover:bg-[#003580] text-white">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const radarData = evaluation.scores.map(score => ({
    evangelist: score.evangelist.split(" ")[1] || score.evangelist.split(" ")[0],
    score: score.score,
    fullMark: 100
  }));

  const totalScore = evaluation.total_score;
  const averageScore = Math.round(totalScore / 9);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-[#E2E8F0] px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/")}
            data-testid="header-title"
          >
            <img src={LOGO_URL} alt="9Mantra Logo" className="h-10 w-auto" />
            <h1 className="text-2xl font-normal text-[#1A1A1A]">9Mantra</h1>
          </div>
          <Button
            onClick={() => navigate("/validate")}
            variant="outline"
            className="border-[#E2E8F0] text-[#1A1A1A] hover:bg-[#F8F9FA]"
            data-testid="new-validation-btn"
          >
            New Validation
          </Button>
        </div>
      </div>

      {/* Results Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Score Overview */}
        <div className="mb-16 text-center" data-testid="score-overview">
          <h2 className="text-5xl md:text-6xl text-[#1A1A1A] mb-4">
            <span className={getScoreColor(averageScore)} data-testid="total-score">{totalScore}</span>
            <span className="text-[#64748B]">/900</span>
          </h2>
          <p className="text-lg text-[#64748B]">Overall Viability Score</p>
          
          {/* Success Predictor */}
          {evaluation.success_predictor && (
            <div className="mt-8 bg-[#F8F9FA] border border-[#E2E8F0] p-6 max-w-2xl mx-auto" data-testid="success-predictor">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Calendar className="h-6 w-6 text-[#0047AB]" />
                <h3 className="text-2xl text-[#1A1A1A]">
                  {evaluation.success_predictor.survival_days} Days
                </h3>
              </div>
              <p className="text-base text-[#64748B] mb-3">
                Estimated Survival (~{Math.round(evaluation.success_predictor.survival_days / 365)} years)
              </p>
              <p className="text-sm text-[#1A1A1A]">
                <span className="font-medium">Confidence:</span> {evaluation.success_predictor.confidence_level}
              </p>
              {evaluation.success_predictor.key_factors && (
                <div className="mt-4 text-left">
                  <p className="text-sm font-medium text-[#1A1A1A] mb-2">Key Factors:</p>
                  <ul className="text-sm text-[#64748B] space-y-1">
                    {evaluation.success_predictor.key_factors.map((factor, idx) => (
                      <li key={idx}>• {factor}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {/* Download PDF Button */}
          <Button
            onClick={handleDownloadPDF}
            className="mt-6 bg-white border border-[#E2E8F0] text-[#0047AB] hover:bg-[#F8F9FA]"
            data-testid="download-pdf-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF Report
          </Button>
        </div>

        {/* Radar Chart */}
        <div className="mb-16 bg-[#F8F9FA] border border-[#E2E8F0] p-8" data-testid="radar-chart">
          <h3 className="text-2xl text-[#1A1A1A] text-center mb-8">9-Point Analysis</h3>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis 
                dataKey="evangelist" 
                tick={{ fill: '#64748B', fontSize: 12 }}
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                tick={{ fill: '#64748B' }}
              />
              <Radar 
                name="Score" 
                dataKey="score" 
                stroke="#0047AB" 
                fill="#0047AB" 
                fillOpacity={0.3}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Executive Summary */}
        <div className="mb-16 bg-white border border-[#E2E8F0] p-8" data-testid="executive-summary">
          <h3 className="text-2xl text-[#1A1A1A] mb-4">Executive Summary</h3>
          <p className="text-[#1A1A1A] leading-relaxed">{evaluation.summary}</p>
        </div>

        {/* Evangelist Scores */}
        <div className="mb-16">
          <h3 className="text-2xl text-[#1A1A1A] mb-8" data-testid="evangelist-scores-heading">
            Detailed Evangelist Scores
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {evaluation.scores.map((score, idx) => (
              <div 
                key={idx} 
                className="bg-white border border-[#E2E8F0] p-6"
                data-testid={`evangelist-score-${idx}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <h4 className="text-lg font-medium text-[#1A1A1A]">{score.evangelist}</h4>
                  <span className={`text-2xl font-semibold ${getScoreColor(score.score)}`}>
                    {score.score}
                  </span>
                </div>
                <p className="text-sm italic text-[#64748B] mb-4">"{score.mantra}"</p>
                <p className="text-sm text-[#1A1A1A] leading-relaxed">{score.reasoning}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Competitor Analysis */}
        {evaluation.competitors && evaluation.competitors.length > 0 && (
          <div className="mb-16">
            <h3 className="text-2xl text-[#1A1A1A] mb-8" data-testid="competitors-heading">
              Competitor Analysis
            </h3>
            <div className="space-y-4">
              {evaluation.competitors.map((competitor, idx) => (
                <div 
                  key={idx} 
                  className="bg-white border border-[#E2E8F0] p-6"
                  data-testid={`competitor-${idx}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="text-lg font-medium text-[#1A1A1A]">{competitor.name}</h4>
                    <div className="flex items-center gap-2">
                      {getThreatIcon(competitor.threat_level)}
                      <span className="text-sm font-medium text-[#64748B]">
                        {competitor.threat_level} Threat
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-[#1A1A1A] mb-3">{competitor.description}</p>
                  <div className="bg-[#F8F9FA] p-3 rounded">
                    <p className="text-sm text-[#64748B]">
                      <span className="font-medium text-[#1A1A1A]">Your Differentiator:</span> {competitor.differentiator}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Research Reports */}
        {evaluation.research_reports && evaluation.research_reports.length > 0 && (
          <div className="mb-16">
            <h3 className="text-2xl text-[#1A1A1A] mb-8" data-testid="research-reports-heading">
              Relevant Research Reports
            </h3>
            <div className="space-y-4">
              {evaluation.research_reports.map((report, idx) => (
                <div 
                  key={idx} 
                  className="bg-white border border-[#E2E8F0] p-6 hover:-translate-y-1 transition-all duration-300"
                  data-testid={`research-report-${idx}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-base font-medium text-[#1A1A1A] flex-1">{report.title}</h4>
                    {report.url && (
                      <a
                        href={report.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#0047AB] hover:text-[#003580] ml-4"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-[#64748B] mb-2">
                    {report.source} • {report.year}
                  </p>
                  <p className="text-sm text-[#1A1A1A]">{report.relevance}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center py-12 border-t border-[#E2E8F0]">
          <h3 className="text-xl text-[#1A1A1A] mb-4">Have another idea to validate?</h3>
          <Button
            onClick={() => navigate("/validate")}
            className="bg-[#0047AB] hover:bg-[#003580] text-white px-8 py-3"
            data-testid="validate-another-btn"
          >
            Validate Another Idea
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResultsDashboard;
